import { notify } from '../assets/helpers/toast';
import { useInboxCountStore } from '../stores/inboxCountStore';
import { useSocketConnectionStore } from '../stores/socketConnectionStore';

let socket = null; // native WebSocket instance

const WS_BASE_URL = process.env.REACT_APP_PUBLIC_WSS; // e.g. wss://api.example.com

const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_DELAY_MS = 3000;

let reconnectAttempts = 0;
let reconnectTimer = null;
let manuallyClosed = false;
let lastConnectArgs = null; // { userId, accessToken }

function buildWsUrl(userId, accessToken) {
  const qs = new URLSearchParams();
  if (userId) qs.set('u', userId);
  if (accessToken) qs.set('t', accessToken);

  // NOTE: This keeps your existing "u/t" convention. If you can, prefer
  // passing tokens via a first message after connect instead of query params.
  const query = qs.toString();
  return `${WS_BASE_URL}${'/web'}?${query}`;
}

function scheduleReconnect() {
  if (manuallyClosed) return;
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return;
  if (reconnectTimer) return;

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    reconnectAttempts += 1;
    if (lastConnectArgs) {
      connectSocket(lastConnectArgs.userId, lastConnectArgs.accessToken);
    }
  }, RECONNECT_DELAY_MS);
}

export function connectSocket(userId, accessToken) {
  lastConnectArgs = { userId, accessToken };
  manuallyClosed = false;

  if (
    socket &&
    (socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING)
  ) {
    useSocketConnectionStore
      .getState()
      .setConnected(socket.readyState === WebSocket.OPEN);
    return socket;
  }

  const url = buildWsUrl(userId, accessToken);
  if (!url) {
    console.error('WebSocket URL is missing. Check REACT_APP_PUBLIC_WSS.');
    return null;
  }

  socket = new WebSocket(url);

  socket.addEventListener('open', () => {
    reconnectAttempts = 0;
    useSocketConnectionStore.getState().setConnected(true);
    console.log('WebSocket connected');
  });

  socket.addEventListener('message', (event) => {
    console.log('WebSocket message:', event?.data);

    try {
      const data =
        typeof event?.data === 'string' ? JSON.parse(event.data) : event?.data;

      if (data?.type === 'saleNotifInsert') {
        notify(`${data?.data?.title}  ${data?.data?.message}`, 'notification');
        useInboxCountStore.getState().fetchInboxCount();
      }
    } catch {
      // Ignore parse errors for non-JSON messages
    }
  });

  socket.addEventListener('close', (event) => {
    useSocketConnectionStore.getState().setConnected(false);
    console.log('WebSocket disconnected:', {
      code: event?.code,
      reason: event?.reason,
      wasClean: event?.wasClean,
    });
    scheduleReconnect();
  });

  socket.addEventListener('error', () => {
    // Browser WS "error" event has very little detail by design.
    console.error('WebSocket error (no details available from browser).');
  });

  return socket;
}

export function disconnectSocket() {
  if (socket) {
    manuallyClosed = true;
    useSocketConnectionStore.getState().setConnected(false);
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    // 1000 = normal closure
    socket.close(1000, 'client disconnect');
    socket = null;
  }
}

export function getSocket() {
  return socket;
}
